/**
 * Script global para solucionar problemas de modales en iOS
 * Este script se carga en el punto de entrada de la aplicación (main.ts)
 * y aplica correcciones automáticas para problemas específicos de iOS
 */

export class IosModalFix {
  private static isIOS: boolean = false;

  /**
   * Inicializa y aplica los parches necesarios
   */
  public static initializePatches(): void {
    // Detectar si es un dispositivo iOS
    this.detectiOS();

    if (this.isIOS) {
      console.log(
        'Dispositivo iOS detectado: aplicando correcciones automáticas para modales'
      );

      // Aplicar todos los parches
      this.patchViewportHeight();
      this.patchStyleTags();
      this.patchModalBehavior();
      this.observeDOMForNewModals();
    } else {
      // Asegurarse de que no tenga la clase ios-device en dispositivos no-iOS
      document.documentElement.classList.remove('ios-device');
      document.body.classList.remove('ios-device');
      console.log('Dispositivo no-iOS: manteniendo animaciones originales');
    }
  }

  /**
   * Detecta si el dispositivo utiliza iOS
   */
  private static detectiOS(): void {
    const userAgent = window.navigator.userAgent;
    const isIOSDevice =
      /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;

    // También detectar Safari en cualquier plataforma
    const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);
    const isMac = /Mac/.test(userAgent);
    const isSafariOnMac = isSafari && isMac;

    // En entornos de desarrollo, posibilidad de forzar el modo iOS para pruebas
    const forceIOSMode = window.localStorage.getItem('forceIOSMode') === 'true';

    this.isIOS = isIOSDevice || isSafariOnMac || forceIOSMode;

    if (this.isIOS) {
      document.documentElement.classList.add('ios-device');
      document.body.classList.add('ios-device');
    }
  }

  /**
   * Soluciona el problema del 100vh en iOS
   */
  private static patchViewportHeight(): void {
    const setVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    // Ejecutar inmediatamente y luego en resize/rotación
    setVh();
    window.addEventListener('resize', setVh);
    window.addEventListener('orientationchange', setVh);
  }

  /**
   * Agrega reglas CSS adicionales para dispositivos iOS
   */
  private static patchStyleTags(): void {
    const style = document.createElement('style');
    style.innerHTML = `
      /* iOS specific fixes */
      .ios-device .modal-overlay,
      .ios-device .custom-modal-overlay,
      .ios-device .large-modal-overlay,
      .ios-device .diagnosis-modal-overlay {
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        height: 100% !important;
        height: calc(var(--vh, 1vh) * 100) !important;
        z-index: 9999 !important;
        -webkit-transform: translateZ(0) !important;
        transform: translateZ(0) !important;
        -webkit-backface-visibility: hidden !important;
        backface-visibility: hidden !important;
        -webkit-overflow-scrolling: touch !important;
      }
      
      /* Modo de desarrollo - botón para alternar modo iOS */
      #toggle-ios-mode {
        position: fixed;
        bottom: 10px;
        left: 10px;
        background: rgba(0,0,0,0.5);
        color: white;
        border: none;
        padding: 5px 10px;
        z-index: 99999;
        font-size: 10px;
        border-radius: 4px;
        cursor: pointer;
      }
    `;
    document.head.appendChild(style);

    // Agregar botón de desarrollo para alternar modo iOS (solo en desarrollo)
    if (
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1'
    ) {
      this.addIOSToggleButton();
    }
  }

  /**
   * Agrega un botón para alternar el modo iOS (solo para desarrollo)
   */
  private static addIOSToggleButton(): void {
    const toggleButton = document.createElement('button');
    toggleButton.id = 'toggle-ios-mode';
    toggleButton.innerText = this.isIOS
      ? 'Desactivar modo iOS'
      : 'Activar modo iOS';

    toggleButton.addEventListener('click', () => {
      const currentValue =
        window.localStorage.getItem('forceIOSMode') === 'true';
      window.localStorage.setItem('forceIOSMode', (!currentValue).toString());

      // Recargar la página para aplicar el cambio
      window.location.reload();
    });

    document.body.appendChild(toggleButton);
  }

  /**
   * Parcha el comportamiento de los modales para iOS
   */
  private static patchModalBehavior(): void {
    // Prevenir scroll del body cuando un modal está abierto
    document.addEventListener(
      'touchmove',
      (e) => {
        const target = e.target as HTMLElement;
        const isModal = !!target.closest(
          '.modal-overlay, .custom-modal-overlay, .large-modal-overlay, .diagnosis-modal-overlay'
        );
        const isModalContent = !!target.closest(
          '.modal-content, .custom-modal-content, .large-modal-content, .diagnosis-modal-content'
        );

        // Si estamos en un modal pero no en el contenido del modal (que puede tener scroll propio)
        if (isModal && !isModalContent) {
          e.preventDefault();
        }
      },
      { passive: false }
    );

    // Prevenir zoom en doble tap
    document.addEventListener(
      'gesturestart',
      (e) => {
        e.preventDefault();
      },
      { passive: false }
    );
  }

  /**
   * Observa el DOM para detectar nuevos modales y aplicarles correcciones
   */
  private static observeDOMForNewModals(): void {
    // Crear un observador para detectar cuando se añaden nuevos modales al DOM
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as HTMLElement;

              // Si el elemento añadido es un modal o contiene un modal
              const modals = [
                ...Array.from(element.querySelectorAll('.modal-overlay')),
                ...Array.from(
                  element.querySelectorAll('.custom-modal-overlay')
                ),
                ...Array.from(element.querySelectorAll('.large-modal-overlay')),
                ...Array.from(
                  element.querySelectorAll('.diagnosis-modal-overlay')
                ),
              ];

              // Si el propio elemento es un modal
              if (
                element.classList.contains('modal-overlay') ||
                element.classList.contains('custom-modal-overlay') ||
                element.classList.contains('large-modal-overlay') ||
                element.classList.contains('diagnosis-modal-overlay')
              ) {
                modals.push(element);
              }

              // Aplicar fixes a todos los modales detectados
              modals.forEach((modal) => {
                this.fixModalElement(modal as HTMLElement);
              });
            }
          });
        }
      });
    });

    // Iniciar observación del body
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Aplica correcciones a un elemento modal específico
   */
  private static fixModalElement(element: HTMLElement): void {
    // Forzar repaint
    void element.offsetHeight;

    // Mover al body si no es hijo directo
    if (element.parentElement !== document.body) {
      document.body.appendChild(element);
    }

    // Aplicar estilos específicos solo si estamos en iOS
    if (this.isIOS) {
      element.style.zIndex = '9999';
      element.style.transform = 'translateZ(0)';
      element.style.webkitTransform = 'translateZ(0)';
      element.style.willChange = 'transform';
      element.style.backfaceVisibility = 'hidden';
      element.style.webkitBackfaceVisibility = 'hidden';
    }
  }
}
